import React, { FC, useEffect } from 'react';
import { uniqBy } from 'lodash';
import { ApplicantModal } from '@src/containers/ApplicantModal';
import {
  FeaturedTalentCardFragment,
  useGetVerifiedUserForFeaturedTalentLazyQuery,
  useRejectVerifiedUserMutation,
  useSessionQuery,
} from '@src/graphql/generated';
import { DEFAULT_CURRENCY_CODE } from '@src/constants';
import { MetricContext } from '@src/metrics/metrics.enum';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { useGetOpeningRecommendedUsers } from '../../InviteCandidates/hooks/useGetOpeningRecommendedUsers';
import { useGetOpeningId } from '../../hooks/useGetOpeningId';
import { QueryParams } from '../../interfaces/queryParams.interface';
import { ModalProps } from './props';

export const Modal: FC<ModalProps> = ({
  excludedUserIds,
  onConfirmTalent,
  customPitchMessage,
  onCloseModal,
  userId,
  isDirectConnectShownDefault,
  updateApplicantsCountOnHide,
}) => {
  const { queryParams, updateQueryParams } = useQueryParams<QueryParams>();

  const openingId = useGetOpeningId();

  const { data: sessionData } = useSessionQuery();
  const recruiter = sessionData?.currentRecruiter;

  const [fetchVerifiedUser, { data: verifiedUserData, loading: isSingleUserDataLoading }] =
    useGetVerifiedUserForFeaturedTalentLazyQuery({
      fetchPolicy: 'network-only',
    });

  const {
    isOpeningRecommendedUsersLoading,
    loadMoreRecommendedUsers,
    recommendedUsers,
    recommendedUsersPagination,
    isOpeningRecommendedUsersInitialLoading,
    openingState,
    removeVerifiedUser,
  } = useGetOpeningRecommendedUsers({
    excludedUserIds: excludedUserIds,
  });

  const [rejectVerifiedUser] = useRejectVerifiedUserMutation();

  const handleDiscard = async (userId: string): Promise<void> => {
    await rejectVerifiedUser({
      variables: {
        openingId: openingId,
        userId: userId,
      },
    });

    removeVerifiedUser(userId);

    updateApplicantsCountOnHide();
  };

  const handleSlideChange = (userItem: FeaturedTalentCardFragment): void => {
    if (queryParams?.userId !== userItem?.user?.id) {
      updateQueryParams({
        userId: userItem?.user?.id,
      });
    }
  };

  useEffect(() => {
    if (queryParams.userId) {
      fetchVerifiedUser({
        variables: {
          recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
          userId: queryParams.userId,
        },
      });
    }
  }, []);

  const feedData =
    !(isOpeningRecommendedUsersLoading || isSingleUserDataLoading) &&
    verifiedUserData &&
    !verifiedUserData?.verifiedUser.recruiterState.hasSeen &&
    !recommendedUsers.find(applicant => applicant.user.id === queryParams.userId)
      ? uniqBy([verifiedUserData?.verifiedUser, ...recommendedUsers], 'user.id')
      : recommendedUsers;

  if (recommendedUsers.length === 0) {
    return null;
  }

  return (
    <div>
      <ApplicantModal
        activeUserId={userId}
        areVerificationStagesHidden
        customPitchMessage={customPitchMessage}
        feed={feedData}
        fetchMore={loadMoreRecommendedUsers}
        hasMoreResults={!!recommendedUsersPagination?.hasMoreResults}
        isDirectConnectShownDefault={isDirectConnectShownDefault}
        isInitialLoading={isOpeningRecommendedUsersInitialLoading}
        isLoading={isOpeningRecommendedUsersLoading}
        metricContext={MetricContext.OPENING_VIEW}
        onCloseModal={onCloseModal}
        onConfirmTalent={onConfirmTalent}
        onDiscard={handleDiscard}
        onSlideChange={handleSlideChange}
        openingId={openingId}
        openingState={openingState}
      />
    </div>
  );
};
